import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import AadharCardTemplate from "../templates/AadharCardTemplate";
import { useAuth } from "../../contexts/AuthContext";
import { useSchools } from "../../hooks/useSchools";
import { useSchoolIdForAdmin } from "../../hooks/useSchoolIdForAdmin";
import { useTenantHeader } from "../../hooks/useTenantHeader";
import { useStudents, Student } from "../../hooks/useStudents";
import { useSessions } from "../../hooks/useSession";
import { useClasses } from "../../hooks/useClasses";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { getStudentData } from "../../utils/storage";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";


interface AadharCardFormModalProps {
  open: boolean;
  handleClose: () => void;
  selectedClassId?: number;
  selectedSessionId?: number;
}

const AadharCardFormModal: React.FC<AadharCardFormModalProps> = ({
  open,
  handleClose,
  selectedClassId,
  selectedSessionId,
}) => {
  const { t } = useTranslation();
  const { user, isSchoolAdmin } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const { useSchoolById } = useSchools(false, { disableSchoolAdmins: true });
  const { tenantLoading } = useTenantHeader();

  // For students, get current logged-in student data
  const { studentData: currentStudentData, studentLoading: currentStudentLoading } = useCurrentStudent();

  const [error, setError] = useState("");
  const [showTemplate, setShowTemplate] = useState(false);
  const [studentData, setStudentData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Student selection states
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  // Get school ID for school admin users
  const shouldFetchAdminSchoolId = !!user && isSchoolAdmin() && !user.schoolId;
  const adminSchoolIdQuery = useSchoolIdForAdmin(shouldFetchAdminSchoolId ? user?.id : undefined);

  // Determine effective schoolId to use
  const effectiveSchoolId = user?.schoolId ?? adminSchoolIdQuery.data ?? undefined;

  // Fetch the school by schoolId
  const { data: school } = useSchoolById(effectiveSchoolId);

  // Get students hook - use filtered API when both session and class are selected
  const {
    studentsQuery,
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
    getStudentByStudentCode,
  } = useStudents(false, effectiveSchoolId);

  // Fallback to all students when session/class are not selected
  const allStudents = studentsQuery.data;
  const isLoadingAllStudents = studentsQuery.isLoading;
  const errorAllStudents = studentsQuery.error;

  const { sessionsQuery } = useSessions(effectiveSchoolId);
  const { classesQuery } = useClasses(effectiveSchoolId);

  const sessionOptions = sessionsQuery.data || [];
  const classOptions = classesQuery.data || [];

  const {
    selectedSession: contextSelectedSession,
    setSelectedSession: setContextSelectedSession,
    selectedClass: contextSelectedClass,
    setSelectedClass: setContextSelectedClass,
  } = useSessionClass();

  const coerceId = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isNaN(value) ? null : value;
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const sessionIdFromProps = coerceId(selectedSessionId);
  const classIdFromProps = coerceId(selectedClassId);
  const sessionIdFromContext = coerceId(contextSelectedSession?.id);
  const classIdFromContext = coerceId(contextSelectedClass?.id);

  const normalizedSessionId =
    sessionIdFromProps !== null ? sessionIdFromProps : sessionIdFromContext;

  const normalizedClassId =
    classIdFromProps !== null ? classIdFromProps : classIdFromContext;

  const hasSessionFilter = normalizedSessionId !== null;
  const classNameFilter =
    contextSelectedClass?.section === "__CLASS_ALL__"
      ? "Class-ALL"
      : contextSelectedClass?.section === "__GRADE_ALL__"
        ? `${String(contextSelectedClass?.name || "").trim()}-ALL`
        : String(contextSelectedClass?.name || "").includes("-ALL")
          ? String(contextSelectedClass?.name || "")
          : null;

  const effectiveClassId = normalizedClassId != null && normalizedClassId > 0 ? normalizedClassId : null;

  const hasClassFilter = effectiveClassId !== null || !!classNameFilter;

  // Determine which query to use based on whether both session and class are selected
  const shouldUseFilteredQuery = normalizedSessionId !== null && hasClassFilter;

  const studentsByIdQuery = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : (shouldUseFilteredQuery ? (normalizedSessionId ?? undefined) : undefined),
    isStudent ? undefined : (shouldUseFilteredQuery ? (effectiveClassId ?? undefined) : undefined)
  );

  const studentsByNameQuery = useStudentsBySessionIdAndClassName(
    isStudent ? undefined : (shouldUseFilteredQuery ? (normalizedSessionId ?? undefined) : undefined),
    isStudent ? undefined : (shouldUseFilteredQuery ? (classNameFilter ?? undefined) : undefined)
  );

  const activeStudentsQuery = classNameFilter ? studentsByNameQuery : studentsByIdQuery;

  const {
    data: filteredStudents = [],
    isLoading: isLoadingFilteredStudents,
    error: errorFilteredStudents
  } = activeStudentsQuery;

  const getClassSortValue = (className?: string) => {
    if (!className) return Number.MAX_SAFE_INTEGER;
    const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

    // Ensure Nursery -> LKG -> UKG always appear before Class 1.
    if (normalized.includes("nursery")) return -3;
    if (normalized.includes("lkg")) return -2;
    if (normalized.includes("ukg")) return -1;

    const numericMatch = className.match(/\d+/);
    if (numericMatch) return parseInt(numericMatch[0], 10);

    return Number.MAX_SAFE_INTEGER;
  };

  const getSectionSortValue = (section?: string | null) => {
    if (!section) return 0;
    const normalized = section.trim().toUpperCase();
    if (normalized.length === 0) return 0;

    const charCode = normalized.charCodeAt(0);
    if (charCode >= 65 && charCode <= 90) {
      return charCode - 64; // A=1, B=2, C=3, etc.
    }
    return Number.MAX_SAFE_INTEGER;
  };

  const sortedClassOptions = useMemo(() => {
    const classes = Array.isArray(classOptions) ? classOptions : [];
    return [...classes].sort((a: any, b: any) => {
      const classDiff = getClassSortValue(a?.name) - getClassSortValue(b?.name);
      if (classDiff !== 0) return classDiff;

      const sectionDiff = getSectionSortValue(a?.section) - getSectionSortValue(b?.section);
      if (sectionDiff !== 0) return sectionDiff;

      return (a?.name || "").localeCompare(b?.name || "");
    });
  }, [classOptions]);

  const combinedClassOptions = useMemo(() => {
    const base = sortedClassOptions;
    if (!base.length) return [] as any[];
    const gradeNames = Array.from(new Set(base.map((c: any) => c?.name).filter(Boolean))) as string[];
    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

    const schoolIdFallback = base[0]?.schoolId ?? 0;
    const out: any[] = [
      { id: "Class-ALL", name: "Class", section: "__CLASS_ALL__", schoolId: schoolIdFallback },
    ];

    for (const grade of gradeNames) {
      out.push({ id: `${grade}-ALL`, name: grade, section: "__GRADE_ALL__", schoolId: schoolIdFallback });
      out.push(...base.filter((c: any) => c?.name === grade));
    }
    return out;
  }, [sortedClassOptions]);

  const filteredClasses = combinedClassOptions;

  // Use filtered students from API when both session and class are selected, otherwise filter all students
  const sortedStudents = useMemo(() => {
    // If both session and class are selected, use the filtered API results
    if (shouldUseFilteredQuery) {
      if (!filteredStudents || filteredStudents.length === 0) return [];

      return [...filteredStudents].sort((a, b) => {
        const getStudentName = (student: any) => {
          if (student.studentName) {
            return student.studentName;
          } else if (student.firstName && student.lastName) {
            return `${student.firstName} ${student.lastName}`;
          } else if (student.firstName) {
            return student.firstName;
          } else if (student.lastName) {
            return student.lastName;
          } else {
            return t("unknown_student");
          }
        };

        const nameA = getStudentName(a).toLowerCase();
        const nameB = getStudentName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    // Otherwise, use the old filtering logic for backward compatibility
    if (!allStudents) return [];

    let filtered = allStudents;

    if (hasSessionFilter) {
      filtered = filtered.filter(
        (student: any) => coerceId(student.sessionId) === normalizedSessionId
      );
    }

    if (hasClassFilter && effectiveClassId !== null) {
      filtered = filtered.filter((student: any) => coerceId(student.schoolClassId) === effectiveClassId);
    }

    return [...filtered].sort((a, b) => {
      const getStudentName = (student: any) => {
        if (student.studentName) {
          return student.studentName;
        } else if (student.firstName && student.lastName) {
          return `${student.firstName} ${student.lastName}`;
        } else if (student.firstName) {
          return student.firstName;
        } else if (student.lastName) {
          return student.lastName;
        } else {
          return t("unknown_student");
        }
      };

      const nameA = getStudentName(a).toLowerCase();
      const nameB = getStudentName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [
    shouldUseFilteredQuery,
    filteredStudents,
    allStudents,
    hasSessionFilter,
    hasClassFilter,
    normalizedSessionId,
    normalizedClassId,
    t
  ]);

  useEffect(() => {
    if (sessionIdFromProps !== null && sessionOptions.length > 0) {
      const sessionObj = sessionOptions.find(
        (session: any) => coerceId(session.id) === sessionIdFromProps
      );
      if (
        sessionObj &&
        (!contextSelectedSession ||
          coerceId(contextSelectedSession.id) !== sessionIdFromProps)
      ) {
        setContextSelectedSession(sessionObj);
      }
    }
  }, [
    sessionIdFromProps,
    sessionOptions,
    contextSelectedSession,
    setContextSelectedSession,
  ]);

  useEffect(() => {
    if (classIdFromProps !== null && classOptions.length > 0) {
      const classObj = classOptions.find(
        (cls: any) => coerceId(cls.id) === classIdFromProps
      );
      if (
        classObj &&
        (!contextSelectedClass ||
          coerceId(contextSelectedClass.id) !== classIdFromProps)
      ) {
        setContextSelectedClass(classObj);
      }
    }
  }, [
    classIdFromProps,
    classOptions,
    contextSelectedClass,
    setContextSelectedClass,
  ]);

  // Auto-load student data for students when modal opens - skip form UI
  useEffect(() => {
    if (isStudent && open) {
      // Use currentStudentData from useCurrentStudent hook
      if (currentStudentData && !currentStudentLoading) {
        setIsLoading(true);
        setError('');
        (async () => {
          try {
            // Use studentCode to fetch the complete student data if available
            let completeStudent = currentStudentData;
            if (currentStudentData.studentCode) {
              try {
                completeStudent = await getStudentByStudentCode(currentStudentData.studentCode);
              } catch (error) {
              }
            }
            const mappedData = mapStudentToAadharCardData(completeStudent);
            setStudentData(mappedData);
            setShowTemplate(true);
            setError("");
          } catch (error: any) {
            setError(t("error_loading_student_data"));
          } finally {
            setIsLoading(false);
          }
        })();
      } else if (currentStudentLoading) {
        setIsLoading(true);
        setError('');
      } else if (!currentStudentData && !currentStudentLoading) {
        setError(t("error_loading_student_data"));
        setIsLoading(false);
      }
    } else if (!isStudent && open) {
      // Reset for non-students
      setSelectedStudentId(null);
      setShowTemplate(false);
      setStudentData(null);
    }
  }, [open, isStudent, currentStudentData, currentStudentLoading, getStudentByStudentCode, t]);

  const handleSessionFilterChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === "") {
        setContextSelectedSession(null);
        setContextSelectedClass(null);
        return;
      }
      const numericValue = Number(value);
      const sessionObj = sessionOptions.find(
        (session: any) => Number(session.id) === numericValue
      );
      setContextSelectedSession(sessionObj || null);
      setContextSelectedClass(null);
    },
    [sessionOptions, setContextSelectedSession, setContextSelectedClass]
  );

  const handleClassFilterChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === "") {
        setContextSelectedClass(null);
        return;
      }
      const vStr = String(value);
      const classObj = (filteredClasses || []).find((cls: any) => String(cls.id) === vStr);
      setContextSelectedClass(classObj || null);
    },
    [filteredClasses, setContextSelectedClass]
  );

  const handleStudentSelect = useCallback((event: any) => {
    const studentId = Number.parseInt(event.target.value, 10);
    if (Number.isNaN(studentId)) {
      setSelectedStudentId(null);
      return;
    }
    setSelectedStudentId(studentId);
    setError(''); // Clear any previous errors
  }, []);

  // Function to map API response data to AadharCardTemplate format
  // Helper function to ensure dates are in YYYY-MM-DD format
  const ensureValidDate = (date: any): string => {
    if (!date) {
      return "";
    }

    // Handle array format dates [year, month, day] - common from APIs
    if (Array.isArray(date) && date.length >= 3) {
      const [year, month, day] = date;
      if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
        // Note: month is 0-indexed in JavaScript Date constructor, but API might send 1-indexed
        // We'll try both approaches
        let parsedDate = new Date(year, month - 1, day); // Assume 1-indexed month
        if (isNaN(parsedDate.getTime())) {
          parsedDate = new Date(year, month, day); // Try 0-indexed month
        }

        if (!isNaN(parsedDate.getTime())) {
          const result = parsedDate.toISOString().split('T')[0];
          return result;
        } else {
        }
      }
    }

    // If it's already a string in YYYY-MM-DD format, return as is
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return date;
    }

    // If it's a Date object, convert to YYYY-MM-DD format
    if (date instanceof Date) {
      const result = date.toISOString().split('T')[0];
      return result;
    }

    // If it's a string in other format, try to parse it
    if (typeof date === 'string') {
      try {
        // Handle different date formats that might come from the API
        let parsedDate: Date;

        // Try parsing as-is first
        parsedDate = new Date(date);

        // If that fails, try some common date formats
        if (isNaN(parsedDate.getTime())) {
          // Try DD/MM/YYYY format
          const ddmmyyyy = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (ddmmyyyy) {
            const [, day, month, year] = ddmmyyyy;
            parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
        }

        // If still fails, try DD-MM-YYYY format
        if (isNaN(parsedDate.getTime())) {
          const ddmmyyyy = date.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
          if (ddmmyyyy) {
            const [, day, month, year] = ddmmyyyy;
            parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
        }

        if (!isNaN(parsedDate.getTime())) {
          const result = parsedDate.toISOString().split('T')[0];
          return result;
        } else {
        }
      } catch (error) {
      }
    }

    // If all else fails, return empty string
    return "";
  };

  const mapStudentToAadharCardData = (student: any) => {

    const mappedData = {
      id: student.id, // Add application ID for photo fetching
      dateOfIssue: new Date().toISOString().split('T')[0], // Current date
      isResident: true,
      isNRI: false,
      isNewEnrolment: true,
      isUpdateRequest: false,
      aadhaarNumber: student.aadharNo || "",
      fullName: student.studentName || "",
      careOf: student.fatherName || "", // Using father's name as care of
      houseNo: student.houseNo || "",
      street: student.street || "",
      landmark: student.landmark || "",
      area: student.area || "",
      village: student.city || "", // Using city as village
      postOffice: student.postOffice || "",
      district: student.district || "",
      state: student.state || "",
      pinCode: student.pinCode || "",
      dateOfBirth: student.dateOfBirth, // Pass array format directly to avoid timezone issues
      mobileNo: student.mobileNo || "",
      gmail: student.gmail || "",
      certifierName: 'Dr. Rajesh Kumar',
      certifierDesignation: 'Principal',
      certifierOfficeAddress: 'ABC School, Main Street, City Name, State Name',
      certifierContactNumber: '9876543210',
      isGazettedOfficer: false,
      isHeadOfInstitution: false, // Will be controlled by template's local state
    };

    return mappedData;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStudentId || !sortedStudents) {
      setError(t("please_select_student"));
      return;
    }

    const student = sortedStudents.find((s: Student) => s.id === selectedStudentId);

    if (!student) {
      setError(t("student_not_found"));
      return;
    }

    if (!student.studentCode) {
      setError(t("student_code_not_found"));
      return;
    }

    // Set loading state and clear previous errors
    setIsLoading(true);
    setError("");

    try {
      // Use studentCode to fetch the complete student data
      const studentData = await getStudentByStudentCode(student.studentCode);

      if (studentData.id && studentData.studentName) {
        // Student found, show template
        setStudentData(studentData);
        setShowTemplate(true);
        // Clear the form after successful submission
        setError("");
      } else {
        // No student found
        setError(t("no_student_found_student_code"));
      }
    } catch (error: any) {
      // Fallback to using the original student data
      setStudentData(student);
      setShowTemplate(true);
      setError("");
    } finally {
      setIsLoading(false);
    }
  };



  const handleCloseModal = () => {
    setError("");
    setShowTemplate(false);
    setStudentData(null);
    setSelectedStudentId(null); // Reset student selection
    handleClose();
  };

  const handleCloseTemplate = () => {
    setShowTemplate(false);
    setStudentData(null);
    setError("");
  };

  // For students, directly show the template without the selection form
  if (isStudent && open) {
    if (showTemplate && studentData) {
      return (
        <Dialog
          open={open}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: 'sticky',
              top: 0,
              zIndex: 1000,
              backgroundColor: 'white',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t("aadhar_card")}
            </Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ overflow: 'auto', flex: 1 }}>
            <Box sx={{ p: 2 }}>
              <AadharCardTemplate data={studentData} schoolData={school} />
            </Box>
          </DialogContent>
        </Dialog>
      );
    } else if (isLoading) {
      // Show loading state while fetching student data
      return (
        <Dialog
          open={open}
          onClose={handleCloseModal}
          maxWidth="xs"
          fullWidth
        >
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t("loading")}...
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      );
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleCloseModal();
        }
      }}
      maxWidth="xs"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '400px',
          width: '90%'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("get_aadhar_card")}
        </Typography>
        <IconButton onClick={handleCloseModal} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("select_student_for_aadhar_card")}
            </Typography>

            <FormControl
              fullWidth
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  '&.Mui-focused': { color: '#0168EA' },
                },
              }}
              disabled={sessionOptions.length === 0}
            >
              <InputLabel>{t("session")}</InputLabel>
              <Select
                value={normalizedSessionId ?? ""}
                label={t("session")}
                onChange={handleSessionFilterChange}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t("select_session_placeholder", { defaultValue: "Select Session" })}</em>
                </MenuItem>
                {sessionOptions.map((session: any) => (
                  <MenuItem key={session.id} value={Number(session.id)}>
                    {session.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  '&.Mui-focused': { color: '#0168EA' },
                },
              }}
              disabled={!hasSessionFilter || filteredClasses.length === 0}
            >
              <InputLabel>{t("class")}</InputLabel>
              <Select
                value={(() => {
                  if (!contextSelectedClass) return "";
                  if ((contextSelectedClass as any)?.section === "__CLASS_ALL__") return "Class-ALL";
                  if ((contextSelectedClass as any)?.section === "__GRADE_ALL__")
                    return `${String((contextSelectedClass as any)?.name || "").trim()}-ALL`;
                  return contextSelectedClass?.id != null ? String(contextSelectedClass.id) : "";
                })()}
                label={t("class")}
                onChange={handleClassFilterChange}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t("select_class_placeholder", { defaultValue: "Select Class" })}</em>
                </MenuItem>
                {filteredClasses.map((cls: any) => (
                  <MenuItem key={String(cls.id)} value={String(cls.id)}>
                    {cls.section === "__CLASS_ALL__"
                      ? "Class-ALL"
                      : cls.section === "__GRADE_ALL__"
                        ? `${cls.name}-ALL`
                        : cls.section
                          ? `${cls.name} ${cls.section}`
                          : cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Student Selection Dropdown */}
            {((shouldUseFilteredQuery && isLoadingFilteredStudents) || (!shouldUseFilteredQuery && isLoadingAllStudents)) ? (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>{t("loading_students")}...</Typography>
              </Box>
            ) : ((shouldUseFilteredQuery && errorFilteredStudents) || (!shouldUseFilteredQuery && errorAllStudents)) ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {t("failed_to_load_students")}: {(shouldUseFilteredQuery ? errorFilteredStudents : errorAllStudents) instanceof Error ? ((shouldUseFilteredQuery ? errorFilteredStudents : errorAllStudents) as Error).message : t('unknown_error')}
              </Alert>
            ) : sortedStudents && sortedStudents.length > 0 ? (
              <FormControl
                fullWidth
                required
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fb',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0168EA',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0168EA',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: '#0168EA',
                    },
                    '&.MuiInputLabel-shrink': {
                      color: '#0168EA',
                    }
                  }
                }}
              >
                <InputLabel
                  id="select-student-label"
                  sx={{
                    color: '#666',
                    '&.Mui-focused': {
                      color: '#0168EA',
                    },
                    '&.MuiInputLabel-shrink': {
                      color: '#0168EA',
                    }
                  }}
                >
                  {t("select_student")}
                </InputLabel>
                <Select
                  labelId="select-student-label"
                  label={t("select_student")}
                  value={selectedStudentId || ''}
                  onChange={handleStudentSelect}
                  required
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '16.5px 14px',
                      '&:focus': {
                        backgroundColor: 'transparent',
                      }
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: '60vh',
                        '& .MuiMenuItem-root': {
                          py: 1.5,
                          minHeight: '48px',
                          fontSize: '0.95rem'
                        }
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("none")}</em>
                  </MenuItem>
                  {sortedStudents.map((student: any) => (
                    <MenuItem
                      key={student.id}
                      value={student.id}
                      sx={{
                        py: 1.5,
                        minHeight: '48px',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        >
                          {(() => {
                            const name = student.studentName || student.firstName || 'S';
                            return name.charAt(0).toUpperCase();
                          })()}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{
                              fontSize: '0.95rem',
                              lineHeight: 1.2,
                              mb: 0.5
                            }}
                          >
                            {(() => {
                              if (student.studentName) {
                                return student.studentName;
                              } else if (student.firstName && student.lastName) {
                                return `${student.firstName} ${student.lastName}`;
                              } else if (student.firstName) {
                                return student.firstName;
                              } else if (student.lastName) {
                                return student.lastName;
                              } else {
                                return t('unknown_student');
                              }
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : sortedStudents && sortedStudents.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t("no_students_found_school")}
              </Alert>
            ) : null}



            {/* Show warning if no tenant ID available */}
            {!tenantLoading && !school?.tenant?.schemaName && (
              <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography variant="caption" color="error.main">
                  ⚠️ {t("unable_to_determine_tenant")}
                </Typography>
              </Box>
            )}

          </Box>

        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            {t("cancel")}
          </Button>
          <Button
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            type="submit"
            variant="contained"
            color="primary"
            disabled={!!error || !selectedStudentId || isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t("fetching")}...
              </>
            ) : tenantLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t("loading")}...
              </>
            ) : (
              t("get_aadhar_card")
            )}
          </Button>
        </DialogActions>
      </form>

      {/* Template Display */}
      {showTemplate && studentData && (
        <Dialog
          open={showTemplate}
          onClose={handleCloseTemplate}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            flexShrink: 0,
          }}>
            <Typography variant="h6" fontWeight="bold">
              {t("aadhar_card_certificate")} - {studentData.studentName}
            </Typography>
            <IconButton onClick={handleCloseTemplate} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
            <AadharCardTemplate data={mapStudentToAadharCardData(studentData)} schoolData={school} />
          </Box>
        </Dialog>
      )}
    </Dialog>
  );
};

export default AadharCardFormModal;
